import { useRef, useState } from "react";
import { LoaderCircle, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { llmApi } from "@/services/rag-service";
import { getApiErrorMessage } from "./utils/catalog";
import type { LLMModel } from "./types";

type AICatalogConfirmDeleteProps = {
  selectedModel: LLMModel | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const AICatalogConfirmDeleteComp = ({
  selectedModel,
  open,
  onClose,
  onSuccess,
}: AICatalogConfirmDeleteProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const deletePendingRef = useRef(false);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !loading) onClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedModel?.id || deletePendingRef.current) return;

    deletePendingRef.current = true;
    setLoading(true);
    try {
      await llmApi.deleteLLMConfig(selectedModel.id);
      toast.success(t("aiCatalog.modelDeletedSuccess"));
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error deleting model:", error);
      toast.error(
        getApiErrorMessage(error, t("aiCatalog.failedToDeleteModel")),
      );
    } finally {
      deletePendingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="ai-catalog-delete-dialog"
        showCloseButton={false}
        className="w-[450px] max-w-[calc(100vw-2rem)] sm:max-w-[450px]"
        onOpenAutoFocus={(event) => {
          restoreFocusRef.current =
            document.activeElement instanceof HTMLElement
              ? document.activeElement
              : null;
          event.preventDefault();
          cancelButtonRef.current?.focus({ preventScroll: true });
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          restoreFocusRef.current?.focus({ preventScroll: true });
          restoreFocusRef.current = null;
        }}
        onEscapeKeyDown={(event) => {
          if (loading) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (loading) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (loading) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{t("aiCatalog.deleteModel")}</DialogTitle>
          <DialogDescription>
            {t("aiCatalog.deleteModelConfirm", { name: selectedModel?.name })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            ref={cancelButtonRef}
            type="button"
            data-testid="ai-catalog-delete-cancel-button"
            onClick={onClose}
            disabled={loading}
            variant="outline"
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            data-testid="ai-catalog-delete-confirm-button"
            onClick={handleDeleteConfirm}
            variant="destructive"
            disabled={loading}
            aria-label={t("common.delete")}
          >
            {loading ? (
              <LoaderCircle className="animate-spin" aria-hidden="true" />
            ) : (
              <Trash2 aria-hidden="true" />
            )}
            {loading ? t("common.deleting") : t("common.delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AICatalogConfirmDeleteComp;
